import { camelCase } from 'lodash';
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, TextField } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { api, ConfigFieldControl, HoveredCard } from '@sierralabs/core-ui';
import { EntityPicker, useEntityContext } from '@sierralabs/entity-ui';
import { TaskEvent, TaskEventType, TaskStatusVersionCard, useGetTaskEntity, useTaskEntityData } from '@sierralabs/workflow-ui';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteRouteComponents } from '../../hooks/site-route/useSiteRouteComponents';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledAlertTitle = styled(AlertTitle) `
  font-size: 15px;
  font-weight: 600;
`;
export const SiteTaskStatusVersionCard = props => {
    const component = useSubscribeComponentChanges(SiteTaskStatusVersionCardPlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const classes = ['site-task-status-version-card'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const { entityId, entityTable, entityIdColumn, routeParam, redirectRouteParam } = component.props || {};
    const routes = useSiteRouteComponents();
    const redirectRoute = routes.find(route => { var _a; return route.id === ((_a = component.props) === null || _a === void 0 ? void 0 : _a.redirectRouteId); });
    const params = useParams();
    const dataId = routeParam ? params[routeParam] : undefined;
    const { task, taskId, findTask } = useTaskEntityData(entityId || entityTable || '', dataId);
    const { taskEntity } = useGetTaskEntity(taskId, true);
    const taskEntityId = entityIdColumn
        ? taskEntity === null || taskEntity === void 0 ? void 0 : taskEntity[entityIdColumn]
        : undefined;
    const getRedirectPath = (version) => {
        var _a;
        let path = ((_a = redirectRoute === null || redirectRoute === void 0 ? void 0 : redirectRoute.props) === null || _a === void 0 ? void 0 : _a.path) || '';
        if (taskEntityId) {
            path = path.replace(`:${redirectRouteParam}`, taskEntityId);
        }
        const delim = path.includes('?') ? '&' : '?';
        return path + delim + `version=${version}`;
    };
    useEffect(() => {
        // refresh on change status
        return TaskEvent.handle(TaskEventType.ChangeStatus, () => taskId && findTask());
    }, [taskId, findTask]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteTaskStatusVersionCardPlugin }, props),
        editing && !(task && taskEntity) && (React.createElement("div", Object.assign({ className: classes.join(' ') }, hoverProps(component)),
            React.createElement(Alert, { severity: 'info' },
                React.createElement(StyledAlertTitle, null, "Site Edit Mode"),
                "Task Status Version Card is displayed here."))),
        task && taskEntity && (React.createElement(TaskStatusVersionCard, { task: task, taskEntity: taskEntity, cardProps: Object.assign({ className: classes.join(' ') }, hoverProps(component)), renderContent: group => group.version !== task.version && (React.createElement(HoveredCard, { title: `Version ${group.version}`, content: 'View the previous version', actions: React.createElement(Button, { variant: 'text', color: 'primary', component: Link, startIcon: React.createElement(Visibility, null), to: getRedirectPath(group.version) }, "View") })) }))));
};
export const SiteTaskStatusVersionCardConfigurator = props => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { component, remove, update } = useConfigComponent(props.component);
    const { entities = [] } = useEntityContext();
    const taskConfig = api.getEntity('task', 'Task');
    const entity = entities.find(entity => {
        var _a, _b;
        return entity.id
            ? entity.id === ((_a = component.props) === null || _a === void 0 ? void 0 : _a.entityId)
            : entity.tableName === ((_b = component.props) === null || _b === void 0 ? void 0 : _b.entityTable);
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: (_a = component.props) === null || _a === void 0 ? void 0 : _a.routeId, routeParam: (_b = component.props) === null || _b === void 0 ? void 0 : _b.routeParam, siteRoutePickerProps: {
                label: 'Entity Detail Route',
                placeholder: 'Path for Entity detail page'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.routeId', 'props.routeParam').forEach(([path, value]) => update(path, value));
            } }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(EntityPicker, { fullWidth: true, variant: 'standard', label: 'Task Entity', placeholder: 'Select the Task Entity', InputProps: { disableUnderline: true }, value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.entityId) || ((_d = component.props) === null || _d === void 0 ? void 0 : _d.entityTable), filter: entity => {
                    return (!!entity.versionControlled &&
                        (entity.relations || []).some(relation => relation.propertyName === taskConfig.propertyName));
                }, onChange: entity => {
                    if (!entity) {
                        remove('props.entityId');
                        remove('props.entityTable');
                        remove('props.entityIdColumn');
                        return;
                    }
                    const prop = entity.id ? 'entityId' : 'entityTable';
                    const column = entity.columns.find(column => column.primary) ||
                        entity.columns[0];
                    update(`props.${prop}`, entity.id || entity.tableName);
                    if (!column) {
                        remove('props.entityIdColumn');
                    }
                    else {
                        update('props.entityIdColumn', column.name || camelCase(column.displayName));
                    }
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { fullWidth: true, disabled: !entity, value: ((_e = component.props) === null || _e === void 0 ? void 0 : _e.entityIdColumn) || null, options: ((entity === null || entity === void 0 ? void 0 : entity.columns) || []).map(column => column.name || camelCase(column.displayName)), onChange: (_, value) => {
                    if (!value) {
                        remove('props.entityIdColumn');
                    }
                    else {
                        update('props.entityIdColumn', value);
                    }
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, label: 'Entity ID Column', placeholder: 'Select the ID Column', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
        React.createElement(SiteRouteAndParamPicker, { nearest: true, component: component, routeId: (_f = component.props) === null || _f === void 0 ? void 0 : _f.redirectRouteId, routeParam: (_g = component.props) === null || _g === void 0 ? void 0 : _g.redirectRouteParam, siteRoutePickerProps: {
                label: 'Status Log Redirect Route',
                placeholder: 'Path for redirect on click'
            }, textFieldProps: {
                label: 'Redirect Route Param',
                placeholder: 'Route param of redirect route'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.redirectRouteId', 'props.redirectRouteParam').forEach(([path, value]) => update(path, value));
            } })));
};
export const SiteTaskStatusVersionCardPlugin = {
    name: 'Task Status Version Card',
    type: 'site-task-status-version-card',
    component: SiteTaskStatusVersionCard,
    configurator: SiteTaskStatusVersionCardConfigurator
};
//# sourceMappingURL=SiteTaskStatusVersionCardPlugin.js.map